import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, MessageSquare, ShoppingBag, Store, Wrench } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthGate } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { BusinessCard, ListingCard, ServiceCard } from "@/components/cards";
import { VerifBadge, PageLoading, EmptyState } from "@/components/common";

export default function PublicProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const gate = useAuthGate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/auth/profile/${id}`).then(({ data }) => setData(data)).catch(() => setData(false)); }, [id]);
  if (data === null) return <PageLoading />;
  if (data === false) return <div className="py-20 text-center text-muted-foreground" data-testid="profile-not-found">This profile is not available.</div>;
  const p = data.profile;
  const isMe = user && user.id === id;
  const memberSince = p.member_since ? new Date(p.member_since).toLocaleDateString("en-AU", { month: "short", year: "numeric" }) : "";
  const message = async () => {
    if (!gate.require("Create a free account to message members on Mtaa.")) return;
    if (isMe) return;
    try { const { data } = await api.post("/conversations", { recipient_id: id, context_type: "direct", message: `Hi ${p.name}, ` }); nav(`/messages/${data.conversation_id}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="public-profile">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />Back</button>
      <div className="rounded-3xl border border-border bg-card p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-primary/10 font-display text-3xl font-extrabold text-primary">
            {p.avatar_url ? <img src={p.avatar_url} alt={p.name} className="h-full w-full object-cover" /> : (p.name || "U")[0].toUpperCase()}
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold" data-testid="profile-name">{p.name}</h1>
            <p className="text-sm text-muted-foreground">{p.suburb ? `${p.suburb}, ` : ""}{p.state} · Member since {memberSince}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {p.email_verified && <VerifBadge type="email_verified" />}
              {p.phone_verified && <VerifBadge type="phone_verified" />}
              {p.identity_verified && <VerifBadge type="identity_verified" />}
            </div>
          </div>
        </div>
        {p.bio && <p className="mt-3 text-sm text-foreground/80">{p.bio}</p>}
        {!isMe && <Button className="mt-4" onClick={message} data-testid="profile-message-btn"><MessageSquare className="h-4 w-4" />Message</Button>}
      </div>

      {data.listings.length > 0 && (
        <section><h2 className="mb-3 font-display text-lg font-semibold">Active listings</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{data.listings.map((l) => <ListingCard key={l.id} l={l} />)}</div></section>
      )}
      {data.businesses.length > 0 && (
        <section><h2 className="mb-3 font-display text-lg font-semibold">Businesses</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{data.businesses.map((b) => <BusinessCard key={b.id} b={b} />)}</div></section>
      )}
      {data.services.length > 0 && (
        <section><h2 className="mb-3 font-display text-lg font-semibold">Services</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{data.services.map((s) => <ServiceCard key={s.id} s={s} />)}</div></section>
      )}
      {data.listings.length === 0 && data.businesses.length === 0 && data.services.length === 0 && (
        <EmptyState icon={ShoppingBag} title="No public listings yet" subtitle={`${p.name} hasn't posted anything public yet.`} />
      )}
    </div>
  );
}
